import type { ComponentType } from "react";
import {
  toSourceTypeId,
  type SourceTypeId,
} from "@onetasklist/shared/sources/source-settings";

export interface SourceHelpContentProps {
  /** The source this help content is for — lets components shared across sources brand themselves correctly. */
  sourceId: string;
}

/** One entry in a source's "Configuring settings" list — a settings field's label paired with what it does. */
export interface SourceHelpSettingsItem {
  label: string;
  description: string;
  /** Optional sub-heading to group related settings under, e.g. distinct sync modes. Ungrouped items render as a flat list. */
  group?: string;
}

interface SourceHelpRegistration {
  component: ComponentType<SourceHelpContentProps>;
  /** Overrides the default https://help.altus.pro/connections/{id} URL when full docs live elsewhere. */
  docsUrl?: string;
}

const sourceHelp = new Map<SourceTypeId, SourceHelpRegistration>();

export const registerSourceHelp = (
  sourceTypeId: SourceTypeId,
  component: ComponentType<SourceHelpContentProps>,
  docsUrl?: string,
): void => {
  sourceHelp.set(sourceTypeId, { component, docsUrl });
};

export const getSourceHelpContent = (
  sourceTypeId: string,
): ComponentType<SourceHelpContentProps> | null => {
  const normalizedSourceTypeId = toSourceTypeId(sourceTypeId);
  if (!normalizedSourceTypeId) {
    return null;
  }

  return sourceHelp.get(normalizedSourceTypeId)?.component ?? null;
};

/** Always returns a usable URL, falling back to the default help.altus.pro template when nothing is registered. */
export const getSourceHelpDocsUrl = (sourceTypeId: string): string => {
  const normalizedSourceTypeId = toSourceTypeId(sourceTypeId);
  const idForUrl = normalizedSourceTypeId ?? sourceTypeId.toLowerCase();

  return (
    (normalizedSourceTypeId &&
      sourceHelp.get(normalizedSourceTypeId)?.docsUrl) ||
    `https://help.altus.pro/connections/${idForUrl}`
  );
};
